<template>
  <div>
    <WizardBox :title="`New ${orderTypeName}`">
      <template v-slot:tools>
        
      </template>
      <template v-slot:content>
        <FormGroup ref="newOrderForm" :form="orderForm">
          <FormWizard :theme="wizardTheme" ref="orderWizard" :form="orderForm" :submitting="submitting" v-bind:tabList="tabList" :finishDisabled="orderItemListLoading" @finished="handleSubmit(false)" @stepChange="wizardStepChanged">
            <template v-slot:content>
              
              <FormWizardStep index="1" v-bind:active="true">
                <FormUploadDropTarget @filesDropped="$refs.documentUpload.filesDroppedFromTarget($event)">
                  <FormRow label="Type" v-show="!isNCR">
                    <FormOption label="Order" name="type" :setting="OrderTypes.order" v-model="order.type" />
                    <FormOption label="Quote" name="type" :setting="OrderTypes.quote" v-model="order.type" />
                  </FormRow>

                  <FormRow label="Customer" v-show="!isNCR && isCustomerPricingSimple">
                    <FormOption label="Existing" name="customerType" setting="existing" v-model="customerType" />
                    <FormOption label="New" name="customerType" setting="new" v-model="customerType" v-show="userHasCreatePermission(PermissionTypes.ContactPermissionTypes.contact)" />
                  </FormRow>
                  
                  <FormRow label="Customer" v-show="!isNewCustomer">
                    <FormSearchableSelect :options="customerLookup" v-model="order.contactId" field="contactId" placeholder="Customer" />
                  </FormRow>
                  <FormRow label="Customer Contact" v-show="!isNewCustomer">
                    <FormSelect
                      :options="contactPersonLookup"
                      v-model="order.contactPersonId"
                      field="contactPersonId"
                      placeholder="Customer Contact"
                      :addonSlot="userHasUpdatePermission(PermissionTypes.ContactPermissionTypes.contact) && customerSelected">
                      <template v-slot:addon>
                        <FormButton class="btn-primary" @click="newContactPerson">+</FormButton>
                      </template>
                    </FormSelect>
                  </FormRow>

                  <FormPanel title="Custom Contact Person" v-show="!isNewCustomer && customerSelected && isCustomContactPerson && !loadingCustomerData">
                    <FormRow label="Name"><FormInputText placeholder="Name" v-model="order.customContactName" field="customContactName" /></FormRow>
                    <FormRow label="Email"><FormInputEmail placeholder="Email" v-model="order.customContactEmail" field="customContactEmail" /></FormRow>
                    <FormRow label="Mobile"><FormInputText placeholder="Mobile" v-model="order.customContactMobile" field="customContactMobile" /></FormRow>
                    <FormRow label="Details">
                      <FormInputTextArea placeholder="Details" v-model="order.customContactDetails" field="customContactDetails" />
                    </FormRow>
                  </FormPanel>
                  
                  <FormPanel title="New Customer Details" v-show="isNewCustomer">
                    <FormRow label="Customer / Trading Name"><FormInputText placeholder="Customer Name" v-model="order.newCustomerName" field="newCustomerName" /></FormRow>
                  
                    <FormRow label="Title"><FormSelect v-model="order.newCustomerTitle" :options="PersonTitleLookup" field="newCustomerTitle" /></FormRow>
                    <FormRow label="First Name"><FormInputText placeholder="First Name" v-model="order.newCustomerPersonFirstName" field="newCustomerPersonFirstName" /></FormRow>
                    <FormRow label="Last Name"><FormInputText placeholder="Last Name" v-model="order.newCustomerPersonLastName" field="newCustomerPersonLastName" /></FormRow>

                    <FormRow label="Email"><FormInputText placeholder="Email" v-model="order.newCustomerEmail" field="newCustomerEmail" /></FormRow>
                    <FormRow label="Mobile"><FormInputText placeholder="Mobile" v-model="order.newCustomerMobile" field="newCustomerMobile" /></FormRow>
                    <FormRow label="Phone"><FormInputText placeholder="Phone" v-model="order.newCustomerPhone" field="newCustomerPhone" /></FormRow>
                    <FormRow label="Fax"><FormInputText placeholder="Fax" v-model="order.newCustomerFax" field="newCustomerFax" /></FormRow>

                    <FormRow label="Default Sales Rate">
                      <SalesRateSelect v-model="order.newCustomerDefaultSalesRateId" field="newCustomerDefaultSalesRateId" />
                    </FormRow>

                    <FormRow label="Labels"><FormLabelTag popupName="Customer Labels" v-model="order.newCustomerLabelList" labelType="contact" field="newCustomerLabelList" /></FormRow>
                  </FormPanel>

                  <FormRow label="Customer Reference No.">
                    <FormInputText v-model="order.customerRef" field="customerRef" placeholder="Customer Reference" />
                    <!-- Display orders with matching customerRef details for the same customer -->
                    <div class="order-matching-customerref-area">
                      <Spinner v-if="loadingMatchingCustomerRefList" />
                      <FormPanel :title="`Warning: ${matchingCustomerRefTotalCount} order(s) were found with matching customer reference numbers`" class="ml-2 mt-1" v-if="!loadingMatchingCustomerRefList && matchingCustomerRefList.length > 0" small>
                        <ul>
                          <li v-for="order in matchingCustomerRefList" :key="order.id">
                            <LabelBadge :labelClass="`label-order-type-${order.type}`">{{order.type | formatLookupName(OrderTypeLookup)}}</LabelBadge>
                            &nbsp;-&nbsp;
                            <LinkOrder :id="order.id" :name="order.name" />
                            &nbsp;-&nbsp;
                            <LabelBadge :labelClass="`label-order-status-${order.status}`">{{order.status | formatLookupName(OrderStatusTypeLookup)}}</LabelBadge>
                            &nbsp;-&nbsp;
                            {{order.customerRef}}
                          </li>
                        </ul>
                        <div class="my-1" v-if="matchingCustomerRefTotalCount > matchingCustomerRefList.length">+ {{matchingCustomerRefTotalCount - matchingCustomerRefList.length}} more</div>
                        <div class="my-1">
                          <FormCheckbox label="Accept duplicate reference number" v-model="matchingCustomerRefAcceptWarning" />
                        </div>
                      </FormPanel>
                    </div>
                  </FormRow>

                  <FormRow label="Notes">
                    <FormInputTextArea v-model="order.notes" field="notes" placeholder="Customer / Delivery Notes" large />
                  </FormRow>

                  <FormHorizontalRule />

                  <FormRow label="Document Uploads">
                    <FormUpload ref="documentUpload" @change="order.uploadFileList = $event" field="uploadFileList" />
                    <div v-if="isCopiedFromOrder || isNCRFromOrder" class="mt-2">
                      <FormButton class="btn btn-sm btn-default" @click="importSourceOrderDocuments"><i class="far fa-download" /> Import documents from source order</FormButton>
                    </div>
                  </FormRow>

                </FormUploadDropTarget>

              </FormWizardStep>

              <FormWizardStep index="2">
                <span v-show="customerSelected">
                  <OrderItemList
                    ref="orderEntryList"
                    :isNewOrder="true"
                    :editing="true"
                    @update="order.orderItemList = $event"
                    :orderType="order.type"
                    :contactId="customerContactId"
                    :taxRateId="customerTaxRateId"
                    :customerData="customerData"
                    @updateTotals="summaryTotals = $event"
                    :showTotals="!isNCR"
                    :isDespatchDelivery="isDespatchDelivery"
                    :newContactSalesRateId="order.newCustomerDefaultSalesRateId"
                    :deliveryStockId="deliveryStockId"
                    :optionOrderEditorAvailable="isOptionOrderEditorAvailable"
                    @startOrderEditor="startOrderEditor"
                    @loadingStatusUpdate="orderItemListLoading = $event"
                  />
                </span>
                <span v-if="!customerSelected">
                  <p class="font-italic text-center">Customer must be selected before continuing</p>
                </span>

                
              </FormWizardStep>

              <FormWizardStep index="3">
                <span v-if="!customerSelected">
                  <p class="font-italic text-center">Customer must be selected before continuing</p>
                </span>
                <div v-show="customerSelected" class="row">
                  <div class="col-8">
                    <FormRow label="Despatch Method">
                      <FormOptionGroup field="despatchType">
                        <FormOption label="Delivery" name="despatchType" :setting="DespatchTypes.delivery" v-model="order.despatchType" inline="true" />
                        <FormOption label="Pickup" name="despatchType" :setting="DespatchTypes.pickup" v-model="order.despatchType" inline="true" />
                      </FormOptionGroup>
                      <template v-if="customerDefaultDespatch">
                        <small class="text-muted">Customer Default Despatch Method: <strong>{{customerDefaultDespatch}}</strong></small>
                      </template>
                    </FormRow>

                    <FormRow label="Despatch Location" helpTip="The location for this order. Used for production and despatch planning. Items will be despatched from here">
                      <AccountLocationSelect v-model="order.accountLocationId" field="accountLocationId" />
                      <template v-if="customerDefaultDespatch">
                        <small class="text-muted">Customer Default Despatch Location: <strong>{{customerDefaultLocation}}</strong></small>
                      </template>
                    </FormRow>

                    <FormHorizontalRule v-if="isDespatchDelivery" />

                    <div class="form-group row" v-show="isDespatchDelivery">
                      <label class="col-sm-2 col-form-label">Delivery Fee</label>
                      <div class="col-sm-6">
                        <DeliveryStockSearchableSelect v-model="deliveryStockId" placeholder="Delivery Fee Stock Item" :note="deliveryFeeNote" />
                      </div>
                      <div class="col-sm-4" v-if="orderDeliveryItem">
                        <FormInputCurrency v-show="!isDeliveryItemPercentage" :value="orderDeliveryItem.unitPrice" @input="updateDeliveryItemValue" placeholder="Fixed delivery fee" />
                        <FormInputText type="number" v-show="isDeliveryItemPercentage" :value="orderDeliveryItem.unitPrice" @input="updateDeliveryItemValue" placeholder="Delivery levy" addon="%" />
                      </div>
                    </div>
                    <FormRow label="Delivery Instructions" v-if="isDespatchDelivery">
                      <FormInputText placeholder="Instructions" v-model="order.deliveryInstructions" field="deliveryInstructions" note="Additional instructions or contact info displayed on run sheets" />
                    </FormRow>

                    <FormHorizontalRule v-if="isDespatchDelivery" />

                    <FormRow label="Delivery Locations" v-show="!isNewCustomer && isDespatchDelivery">
                      <FormSearchableSelect
                        :options="contactAddressLookup"
                        v-model="order.contactAddressId"
                        field="contactAddressId"
                        :optionSlot="true">
                        <template v-slot:option="option">
                          <div class="p-1">
                            <LabelTags :labelList="option.data.deliveryLabelList" class="float-right" />
                            {{option.data.name}}
                          </div>                          
                        </template>
                      </FormSearchableSelect>
                    </FormRow>

                    <div v-show="isDespatchDelivery">
                      <FormRow label="Delivery Labels" v-if="addressDeliveryLabelList.length > 0">
                        <LabelTags :labelList="addressDeliveryLabelList" />
                      </FormRow>
                      <FormHorizontalRule />
                      <FormRow label="Delivery Address 1">
                        <FormInputAddressSearch
                          placeholder="Address 1"
                          v-model="order.deliveryAddress1"
                          field="deliveryAddress1"
                          :readonly="!isAddressEditable"
                          @updateAddress2="order.deliveryAddress2 = $event"
                          @updateSuburb="order.deliverySuburb = $event"
                          @updateState="order.deliveryState = $event"
                          @updatePostcode="order.deliveryPostcode = $event"
                          @updateCountry="order.deliveryCountry = $event" />
                      </FormRow>
                      <FormRow label="Delivery Address 2">
                        <FormInputText placeholder="Address 2" v-model="order.deliveryAddress2" field="deliveryAddress2" :readonly="!isAddressEditable" />
                      </FormRow>
                      <FormRow label="Delivery Suburb">
                        <FormInputText placeholder="Suburb" v-model="order.deliverySuburb" field="deliverySuburb" :readonly="!isAddressEditable" />
                      </FormRow>
                      <FormRow label="Delivery State">
                        <FormInputText placeholder="State" v-model="order.deliveryState" field="deliveryState" :readonly="!isAddressEditable" />
                      </FormRow>
                      <FormRow label="Delivery Postcode">
                        <FormInputText placeholder="Postcode" v-model="order.deliveryPostcode" field="deliveryPostcode" :readonly="!isAddressEditable" />
                      </FormRow>
                      <FormRow label="Delivery Country">
                        <FormInputText placeholder="Country" v-model="order.deliveryCountry" field="deliveryCountry" :readonly="!isAddressEditable" />
                      </FormRow>
                      
                    </div>
                  </div>
                  <div class="col-4">
                    <h3 class="mb-0">Estimate</h3>
                    <div v-show="isQuote">
                      <p class="text-muted">Estimates and date requests not available for quotes</p>
                    </div>
                    <div v-show="!isQuote">
                      <small class="text-muted" v-if="estimates.afterDayCutoff">Note: Order is after the order cutoff time for today. Processing schedule will start the following day</small>
                      <small class="text-muted" v-if="isNCR">Note: NCR orders will be completed in production with top priority</small>
                      <div class="my-3"> </div>

                      <div :class="{'row': true, 'font-weight-bold': isDespatchDelivery && !order.customerRequestedDate, 'text-muted': !isDespatchDelivery || order.customerRequestedDate}">
                        <div class="col-6">Delivery</div>
                        <div class="col-6" v-if="estimates.estimatedDeliveryDate && !estimates.loadingError && !estimates.loadingEstimates && !isNCR">{{estimates.estimatedDeliveryDate | formatDate}}</div>
                        <div class="col-6" v-if="!estimates.loadingError && !estimates.loadingEstimates && isNCR">ASAP</div>
                        <div class="col-6 text-danger" v-if="estimates.loadingError">
                          <small>{{estimates.loadingError}}</small><br />
                          <small v-if="estimates.loadingErrorDetails"><i class="fas fa-caret-right"></i> {{estimates.loadingErrorDetails}}</small>
                        </div>
                        <div class="col-6" v-if="estimates.loadingEstimates"><Spinner /></div>
                      </div>
                      <div :class="{'row': true, 'mt-4': true, 'font-weight-bold': isDespatchPickup && !order.customerRequestedDate, 'text-muted': !isDespatchPickup || order.customerRequestedDate}">
                        <div class="col-6">Pickup</div>
                        <div class="col-6" v-if="estimates.estimatedDeliveryDate && !estimates.loadingError && !estimates.loadingEstimates && !isNCR">{{estimates.estimatedPickupDate | formatDate}}</div>
                        <div class="col-6" v-if="!estimates.loadingError && !estimates.loadingEstimates && isNCR">ASAP</div>
                        <div class="col-6 text-danger" v-if="estimates.loadingError">
                          <small>{{estimates.loadingError}}</small><br />
                          <small v-if="estimates.loadingErrorDetails"><i class="fas fa-caret-right"></i> {{estimates.loadingErrorDetails}}</small>
                        </div>
                        <div class="col-6" v-if="estimates.loadingEstimates"><Spinner /></div>
                      </div>
                      <div class="mt-4" v-if="estimates.requiresProductionTransfers">
                          <Icon icon="fa-random" iconClass="order-item-icon-note" class="mr-1" fixedWidth></Icon>
                          {{orderTypeName}} has items that will require <strong>transfers</strong> between locations during production
                      </div>
                      <div class="mt-1" v-if="estimates.requiresDespatchTransfer">
                          <Icon icon="fa-exchange" iconClass="order-item-icon-note" class="mr-1" fixedWidth></Icon>
                          {{orderTypeName}} has items that will require <strong>transfer</strong> to the despatch depot after production
                      </div>
                      <FormHorizontalRule />
                      <div class="row mt-2">
                        <div :class="{'col-6': true, 'font-weight-bold': order.customerRequestedDate}">Requested Date</div>
                        <div class="col-6"><FormCheckbox v-model="order.customerRequestedDate" /></div>
                      </div>
                      <div class="row mt-1" v-show="order.customerRequestedDate">
                        <div class="offset-1 col-2 col-form-label">Date</div>
                        <div class="col-6">
                          <FormDate v-model="order.requiredEarlierDate" field="requiredEarlierDate" />
                        </div>
                      </div>
                      <div class="row mt-1" v-show="order.customerRequestedDate">
                        <div class="offset-1 col-2 col-form-label">Time</div>
                        <div class="col-6">
                          <FormTime v-model="order.requiredEarlierTime" field="requiredEarlierTime" />
                        </div>
                      </div>
                      <div class="row mt-4" v-show="order.customerRequestedDate">
                        <div class="offset-1 col-5">Set Urgent Order</div>
                        <div class="col-6"><FormCheckbox v-model="order.isUrgent" /></div>
                      </div>
                      <div class="row mt-2" v-show="order.customerRequestedDate && order.isUrgent">
                        <div class="offset-1 col-11">
                          <FormInputTextArea v-model="order.urgentReason" field="urgentReason" placeholder="Urgent Order Reason" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </FormWizardStep>

              <FormWizardStep index="4">
                <span v-if="!customerSelected">
                  <p class="font-italic text-center">Customer must be selected before continuing</p>
                </span>
                <div class="row review-data" v-if="customerSelected">
                  <div class="col-9">
                    <div class="row">
                      <div class="col-6">
                        <div class="review-header">
                          <h2>{{orderTypeName}} Summary</h2> <a class="review-edit-button" @click="displayDetailsTab"><i class="far fa-pencil"></i></a>
                        </div>
                        <FormRow label="Type" :labelColumnSize="5">
                          <LabelBadge :class="`label-order-type-${order.type}`">{{orderTypeName}}</LabelBadge>
                          <LabelBadge labelClass="label-order-urgent" class="ml-1" v-if="order.isUrgent">Urgent</LabelBadge>
                        </FormRow>
                        <FormRow label="Customer Reference No." :labelColumnSize="5"><FormStaticLabel>{{order.customerRef | formatHyphenEmptyValue}}</FormStaticLabel></FormRow>
                        <FormRow label="Location" :labelColumnSize="5">
                          <FormStaticLabel>{{formatLookupName(order.accountLocationId, accountLocationLookup)}}</FormStaticLabel>
                        </FormRow>
                        <FormRow label="Notes" :labelColumnSize="5">
                          <div style="white-space: pre;">{{order.notes | formatHyphenEmptyValue}}</div>
                        </FormRow>
                        <FormRow label="Despatch" :labelColumnSize="5" v-if="!isQuote">
                          <FormStaticLabel>
                            {{isDespatchDelivery ? 'Delivery' : ''}}
                            {{isDespatchPickup ? 'Pickup' : ''}}
                            <div v-if="isDespatchDelivery && !estimates.loadingEstimates && !estimates.loadingError && !order.customerRequestedDate">
                              Estimated delivery
                              <span class="font-weight-bold" v-if="!isNCR">{{estimatedDespatchDate | formatDate}}</span>
                              <span class="font-weight-bold" v-if="isNCR">ASAP</span>
                            </div>
                            <div v-if="isDespatchPickup && !estimates.loadingEstimates && !estimates.loadingError && !order.customerRequestedDate">
                              Estimated pickup 
                              <span class="font-weight-bold" v-if="!isNCR">{{estimatedDespatchDate | formatDate}}</span>
                              <span class="font-weight-bold" v-if="isNCR">ASAP</span>
                            </div>
                            
                            <div v-if="order.customerRequestedDate">
                              Required earlier for {{order.despatchType | formatLookupName(DespatchTypeLookup)}} at <span class="font-weight-bold">{{requiredEarlierDateTime | formatDateTime}}</span>
                            </div>
                            <Spinner v-if="estimates.loadingEstimates" />
                            <p v-if="!isDespatchDelivery && !isDespatchPickup" class="text-danger">None selected</p>
                            <div v-if="estimates.loadingError" class="text-danger">
                              <small>{{estimates.loadingError}}</small><br />
                              <small v-if="estimates.loadingErrorDetails"><i class="fas fa-caret-right"></i> {{estimates.loadingErrorDetails}}</small>
                            </div>
                            
                            <div v-if="orderHasNotices"><small class="text-muted">Note: The order has warnings which may effect despatch times</small></div>
                          </FormStaticLabel>
                        </FormRow>
                        <FormRow label="Urgent Details" :labelColumnSize="5" v-if="order.isUrgent">
                          <div style="white-space: pre;">{{order.urgentReason | formatHyphenEmptyValue}}</div>
                        </FormRow>
                      </div>

                      <div class="col-6" v-if="isNewCustomer">
                        <div class="review-header">
                          <h2>New Customer</h2> <a class="review-edit-button" @click="displayDetailsTab"><i class="far fa-pencil"></i></a>
                        </div>
                        <div class="row review-summary-area">
                          <div class="col-6">
                            <p class="font-weight-bold">{{order.newCustomerName}}</p>
                            <p class="font-weight-bold mt-1">Contact</p>
                            <div class="review-contact-person">
                              <p>{{order.newCustomerTitle | formatLookupName(PersonTitleLookup)}} {{order.newCustomerPersonFirstName}} {{order.newCustomerPersonLastName}}</p>
                              <p v-if="order.newCustomerEmail">{{order.newCustomerEmail}}</p>
                              <p v-if="order.newCustomerMobile">{{order.newCustomerMobile}}</p>
                              <p v-if="order.newCustomerPhone">{{order.newCustomerPhone}}</p>
                              <p v-if="order.newCustomerFax">{{order.newCustomerFax}}</p>
                            </div>
                          </div>
                          <div class="col-6">
                            <p class="font-weight-bold">&nbsp;</p>
                            <p class="font-weight-bold">Credit Type</p>
                            <div class="review-contact-credit-info">
                              <p>Pre-paid Account</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="col-6" v-if="!isNewCustomer">
                        <div class="review-header">
                          <h2>Customer</h2> <a class="review-edit-button" @click="displayDetailsTab"><i class="far fa-pencil"></i></a>
                        </div>
                        <div class="row review-summary-area">
                          <div class="col-6">
                            <span class="font-weight-bold"><LinkContact v-if="order.contactId" :id="customerData.id" :name="customerData.name" /></span>
                            <span v-if="!order.contactId">None</span>

                            <div v-if="order.contactId && order.contactPersonId">
                              <p class="font-weight-bold mt-1">Contact</p>
                              <div class="review-contact-person">
                                <p>{{contactPersonData.title | formatLookupName(PersonTitleLookup)}} {{contactPersonData.firstName}} {{contactPersonData.lastName}}</p>
                                <p v-if="contactPersonData.email">{{contactPersonData.email}}</p>
                                <p v-if="contactPersonData.mobile">{{contactPersonData.mobile}}</p>
                                <p v-if="contactPersonData.phone">{{contactPersonData.phone}}</p>
                                <p v-if="contactPersonData.fax">{{contactPersonData.fax}}</p>
                              </div>
                            </div>
                          </div>
                          <div class="col-6">
                            <template v-if="customerData.credit">
                              <p class="font-weight-bold">&nbsp;</p>
                              <p class="font-weight-bold">Credit Type</p>
                              <div class="review-contact-credit-info">
                                <p>{{customerData.credit.creditType | formatLookupName(CreditTypeLookup)}}</p>
                                <span v-if="customerData.credit.creditType == CreditTypes.account">
                                  <p>Credit Status: <span :class="[`status-creditstatustype-${customerData.credit.creditStatus}`]">{{customerData.credit.creditStatus | formatLookupName(CreditStatusLookup)}}</span></p>
                                  <p>Available Credit: {{customerData.credit.availableCredit | formatCurrency}}</p>
                                </span>
                              </div>
                            </template>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="review-header review-header-seperator">
                      <h2>Items</h2> <a class="review-edit-button" @click="displayItemsTab"><i class="far fa-pencil"></i></a>
                    </div>
                    <OrderItemList
                      ref="orderReviewEntryList"
                      :editing="false"
                      :showTotals="false"
                      :value="reviewOrderItemList"
                      :contactId="customerContactId"
                      :taxRateId="customerTaxRateId"
                    />
                    <p class="font-italic text-center text-muted" v-if="reviewOrderItemList.length == 0">No items in the list</p>

                    <div>
                      <div class="row">
                        <div class="col-6" v-if="isDespatchDelivery && !isQuote">
                          <div class="review-header review-header-seperator">
                            <h2>Delivered To</h2> <a class="review-edit-button" @click="displayDespatchTab"><i class="far fa-pencil"></i></a>
                          </div>
                          <LabelTags v-if="addressDeliveryLabelList.length > 0" :labelList="addressDeliveryLabelList" />
                          <AddressDisplay
                            :address1="order.deliveryAddress1"
                            :address2="order.deliveryAddress2"
                            :suburb="order.deliverySuburb"
                            :state="order.deliveryState"
                            :postcode="order.deliveryPostcode"
                            :country="order.deliveryCountry"
                            />
                        </div>
                        <div class="col-6" v-if="orderHasNotices">
                          <div class="review-header review-header-seperator">
                            <h2>Notices</h2>
                          </div>
                          <p v-if="customerApprovalCount > 0" class="mb-1">
                            <Icon icon="fa-user-circle" iconClass="order-item-icon-customer-approval"  class="mr-1" fixedWidth></Icon>
                            {{orderTypeName}} has {{customerApprovalCount}} item{{customerApprovalCount | displayCountPlural}} that will require <strong>customer</strong> approval before processing
                          </p>
                          <p v-if="productionWarningCount > 0" class="mb-1">
                            <Icon iconStyle="fas" icon="fa-exclamation-triangle" iconClass="order-item-icon-production-error" iconStack="fa-circle" iconStackClass="order-item-icon-production-error-bg" class="mr-1"></Icon>
                            {{orderTypeName}} has {{productionWarningCount}} item{{productionWarningCount | displayCountPlural}} that will require <strong>production</strong> approval before processing
                          </p>
                          <p v-if="externalSetupCount > 0" class="mb-1">
                            <Icon iconStyle="far" icon="fa-external-link-square-alt" iconClass="order-item-icon-external" class="mr-1" fixedWidth></Icon>
                            {{orderTypeName}} has {{externalSetupCount}} item{{externalSetupCount | displayCountPlural}} that will require <strong>external supplier</strong> setup before processing
                          </p>
                          <p v-if="order.customerRequestedDate" class="mb-1">
                            <Icon iconStyle="fas" icon="fa-exclamation-triangle" iconClass="order-item-icon-production-error" iconStack="fa-circle" iconStackClass="order-item-icon-production-error-bg" class="mr-1"></Icon>
                            Order has a requested date set and may require <strong>production</strong> approval before processing
                          </p>
                          <p v-if="pricingWarningCount > 0" class="mb-1">
                            <Icon iconStyle="fas" icon="fa-usd-circle" iconClass="order-item-icon-price-error" iconStack="fa-circle" iconStackClass="order-item-icon-price-error-bg" class="mr-1"></Icon>
                            {{orderTypeName}} has {{pricingWarningCount}} item{{pricingWarningCount | displayCountPlural}} that will require <strong>pricing</strong> approval before processing
                          </p>
                          <p v-if="accountCreditWarning" class="mb-1">
                            <Icon iconStyle="far" icon="fa-credit-card" iconClass="status-credit-danger"  class="mr-1" fixedWidth></Icon>
                            {{orderTypeName}} has an account credit warning that will require <strong>account</strong> approval before processing
                          </p>
                          <p v-if="orderHasCustomerRefWarning" class="mb-1">
                            <Icon iconStyle="far" icon="fa-exclamation-triangle" iconClass="text-danger"  class="mr-1" fixedWidth></Icon>
                            {{orderTypeName}} has a duplicate customer reference number warning that needs to be accepted
                          </p>
                          <small class="text-muted">Note: Further approval requirements may be applied during submission</small>
                        </div>

                      </div>
                    </div>

                    <div class="row">
                      <div class="col-6" v-if="order.uploadFileList.length > 0">
                        <div class="review-header review-header-seperator">
                          <h2>Attached Files</h2> <a class="review-edit-button" @click="displayDetailsTab"><i class="far fa-pencil"></i></a>
                        </div>
                        <div v-for="file in order.uploadFileList" :key="file.key">
                          <FileAttachment :file="file" :isTempUpload="true" />
                        </div>
                        
                      </div>
                    </div>

                  </div>
                  <div class="col-3">
                    <div class="review-header">
                      <h2>Total</h2>
                    </div>
                    <div class="review-totals">
                      <div class="row"><div class="col-6">Sub Total</div><div class="col-6 text-right">{{summaryTotals.subTotal | formatCurrency}}</div></div>
                      <div class="row table-totals-row-discount-total" v-if="summaryTotals.discountTotal > 0">
                        <div class="col-6">Included Discounts</div>
                        <div class="col-6 text-right">{{-summaryTotals.discountTotal | formatCurrency}}</div>
                      </div>
                      <div class="row"><div class="col-6">{{taxName}}</div><div class="col-6 text-right">{{summaryTotals.tax | formatCurrency}}</div></div>
                      <div :class="['row', 'font-weight-bold', 'border-top', accountCreditWarning != '' ? 'status-credit-danger' : '']">
                        <div class="col-6">Total</div>
                        <div class="col-6 text-right">{{summaryTotals.total | formatCurrency}}</div>
                      </div>
                      <div v-if="accountCreditWarning" class="row status-credit-danger">
                        <div class="col-12">
                          <small>Warning: {{accountCreditWarning}}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </FormWizardStep>
            </template>
            <template v-slot:finish-button-list>
              <FormWizardButton btnClass="btn-draft" :loading="submitting" @click="handleSubmit(true)">Submit for draft</FormWizardButton>
            </template>
          </FormWizard>
        </FormGroup>
      </template>
    </WizardBox>

    <div class="order-editor-app" v-if="showOrderEditor">
      <OrderEditorApp 
        v-model="order.orderItemList"
        :orderData="order"
        :isNewOrder="true"
        :orderDocumentUploadList="order.uploadFileList"
        :newContactSalesRateId="order.newCustomerDefaultSalesRateId"
        :orderType="order.type"
        :contactId="customerContactId"
        :taxRateId="customerTaxRateId"
        :customerData="customerData"
        @close="finishOrderEditor"
      />
    </div>

  </div>
</template>

<style scoped>
.order-matching-customerref-area {
  min-height: 1.75rem;
  width: 75%;
}
</style>


<script>
import _ from 'lodash'
import moment from 'moment'
import { mapState } from "vuex"
import { v4 as uuidv4 } from 'uuid'

import form from '@/mixins/form'
import detectUnload from '@/mixins/detectUnload'
import orderItemMixin from '@/modules/orders/mixins/orderItemMixin'
import orderItemDiagramMixin from '@/modules/orders/mixins/orderItemDiagramMixin'
import WizardBox from '@/components/wizardBox'

import Utils from '@/utils/utils'

// Contact Dependencies
import ContactPersonDialog from '@/modules/contacts/views/contactPersonDialog.vue'
import ContactAddressDialog from '@/modules/contacts/views/contactAddressDialog.vue'

// Glass Designer Dependencies
import OrderEditorApp from '@glass-designer/views/app'

import SalesRateSelect from '@/modules/pricing/components/salesRateSelect'
import DeliveryStockSearchableSelect from '@/modules/despatch/components/deliveryStockSearchableSelect'
import AccountLocationSelect from '@/modules/admin/components/accountLocationSelect'
import OrderItemList from '@/modules/orders/components/orderItemList'

import { OrderTypes, OrderTypeLookup, DespatchTypes, DespatchTypeLookup, PersonTitleLookup, InventoryStockTypes, CreditTypes, CreditTypeLookup, CreditStatusTypes, CreditStatusLookup, ContactLabelTypes, OrderStatusTypeLookup, UOMTypes, UOMTypeUnitsLookup, PricingSystemTypes, LinkTypes } from '@/store/types'

export default {
  name: 'OrderNew',
  mixins: [
    form,
    detectUnload,
    orderItemMixin,
    orderItemDiagramMixin
  ],
  components: {
    WizardBox,
    SalesRateSelect,
    OrderItemList,
    DeliveryStockSearchableSelect,
    AccountLocationSelect,
    OrderEditorApp
  },
  props: {
  },
  created() {
    this.OrderTypes = OrderTypes
    this.OrderTypeLookup = OrderTypeLookup
    this.DespatchTypes = DespatchTypes
    this.DespatchTypeLookup = DespatchTypeLookup
    this.PersonTitleLookup = PersonTitleLookup
    this.CreditTypes = CreditTypes
    this.CreditTypeLookup = CreditTypeLookup
    this.CreditStatusTypes = CreditStatusTypes
    this.CreditStatusLookup = CreditStatusLookup
    this.OrderStatusTypeLookup = OrderStatusTypeLookup
    this.UOMTypes = UOMTypes

    this.debouncedCustRefQuery = _.debounce(this.queryMatchingCustRef, 250)
    this.debouncedOrderEstimateQuery = _.debounce(this.queryOrderEstimates, 1000, { leading: true })
  },
  beforeRouteLeave (to, from, next) {
    if(!this.$data.isOrderEdited)
      return next(true)
    
    this.confirmMessage(`Cancel ${this.orderTypeName}?`, `You have unsaved changes with the new ${this.orderTypeName}, are you sure you want to leave?`)
    .then(success => {
      next(success)
    })
    .catch(function () {
      next(false)
    })
  },
  data() {
    return {
      orderForm: {},
      order: {
        type: OrderTypes.order,
        despatchType: '',   // Cleared until explicitly set
        customerRef: '',
        submitDraft: false,
        contactId: null,
        contactNam: '',
        contactPersonId: null,
        contactAddressId: null,
        originalOrderId: null,
        accountLocationId: null,

        deliveryAddress1: '',
        deliveryAddress2: '',
        deliverySuburb: '',
        deliveryState: '',
        deliveryPostcode: '',
        deliveryCountry: '',
        deliveryInstructions: '',

        customContactName: '',
        customContactEmail: '',
        customContactMobile: '',
        customContactDetails: '',

        orderItemList: [],
        uploadFileList: [],

        newCustomerDefaultSalesRateId: null,
        newCustomerLabelList: [],

        customerRequestedDate: false,
        requiredEarlierDate: moment().local().toDate().toISOString(),
        requiredEarlierTime: '07:30',
        isUrgent: false,
        urgentReason: '',

        // Start time of the order entry
        // Used for calculating duration the order took to enter for reporting purposes / cancellation charges
        orderEntryStartTime: moment(),

        // The recovery UUID used / recovering from
        // Server will remove the record when the order is successfully created
        recoveryUUID: uuidv4(),

        overrideWarnings: false
      },

      estimates: {
        estimatedPickupDate: null,
        estimatedDeliveryDate: null,
        loadingEstimates: false,
        loadingError: '',
        loadingErrorDetails: '',
        afterDayCutoff: false,
        requiresProductionTransfers: false,
        requiresDespatchTransfer: false
      },

      summaryTotals: {},
      reviewOrderItemList: [],

      customerType: 'existing',
      customerData: {},
      contactPersonData: {},
      deliveryStockId: null,
      accountData: {},
      customerLookup: [],
      accountLocationLookup: [],
      edgeworkConfigList: [],
      orderStampConfig: {},
      taxRateList: [],
      
      loadingCustomerData: false,
      loadingMatchingCustomerRefList: false,

      matchingCustomerRefTotalCount: 0,
      matchingCustomerRefList: [],
      matchingCustomerRefAcceptWarning: false,

      // Order Editor
      showOrderEditor: false,

      // Other
      isOrderEdited: true,
      disableAccountLocationUpdate: false,    // Hack: Extra flag when copying order data
      submitting: false,
      loading: false,
      orderItemListLoading: false
    }
  },
  watch: {
    'order.contactId': function(newVal, oldVal) {
      this.requestCustomerDetails()
      this.debouncedCustRefQuery()
    },
    'order.accountLocationId': function(newVal, oldVal) {
      this.debouncedOrderEstimateQuery()      
    },
    'order.contactAddressId': function(newVal) {
      // Fill in the delivery details
      if(this.customerData.addresses) {
        let address = this.customerData.addresses.find(address => address.id == this.order.contactAddressId)
        if(address) {
          this.order.deliveryAddress1 = address.address1
          this.order.deliveryAddress2 = address.address2
          this.order.deliverySuburb = address.suburb
          this.order.deliveryState = address.state
          this.order.deliveryPostcode = address.postcode
          this.order.deliveryCountry = address.country
        }
      }
    },
    'order.contactPersonId': function(newVal) {
      // Fill in the contact details for review
      this.contactPersonData = {}
      if(this.customerData.persons) {
        let person = this.customerData.persons.find(person => person.id == this.order.contactPersonId)
        if(person) {
          this.contactPersonData = _.clone(person)
        }
      }
    },
    'order.customerRef': function(newVal) {
      this.debouncedCustRefQuery()
    },
    'order.type': function(newVal) {
      this.$store.commit("page/setPageTitle", `New ${this.orderTypeName}`);
    },
    orderHasCustomerRefWarning: function() {
      this.$refs.orderWizard.setWarningCount(0, this.orderHasCustomerRefWarning ? 1 : 0)
    }
  },
  mounted() {
    // Need to load all the data before we can attempt any copy attempts (if requested)
    this.requestData()
    .then(() => {

      // Load the entry list required data
      this.$refs.orderEntryList.requestData()
      .then(() => {

        // Check if we're copying the data from another order
        if(this.$route.params.copyFromOrder) {
          this.copySourceOrder(this.$route.params.sourceOrderId)
        }

        // Check if we're copying the data from another order to create an NCR
        if(this.$route.params.NCRFromOrder) {
          this.ncrFromSourceOrder(this.$route.params.sourceOrderId)
        }

        // Check if we're recovering from an order recovery record
        if(this.$route.params.recoverOrder) {
          this.copyRecoveryOrder(this.$route.params.orderRecoveryId)
        }
      })
    })
  },
  computed: {
    ...mapState({
      accountPricingSystemType: state => state.account.pricingSystemType
    }),
    tabList() {
      var output = [{ title: 'Details' }, { title: 'Items' }, { title: 'Despatch' }, { title: 'Review' }]
      return output
    },
    wizardTheme() {
      switch(this.order.type) {
        case OrderTypes.order:
          return 'wizard-theme-default'
        case OrderTypes.quote:
          return 'wizard-theme-purple'
        case OrderTypes.nonChargeableRework:
          return 'wizard-theme-red'
      }
      return 'wizard-theme-default'
    },
    isQuote() {
      return this.order.type == OrderTypes.quote
    },
    isNCR() {
      return this.order.type == OrderTypes.nonChargeableRework
    },
    orderTypeName() {
      return Utils.getLookupName(OrderTypeLookup, this.order.type)
    },
    taxName() {
      var taxRate = this.taxRateList.find(taxRate => taxRate.id == this.customerData.defaultTaxRateId)
      if(taxRate)
        return taxRate.displayName

      return this.$store.state.account.salesTaxRate.displayName
    },
    customerSelected() {
      return (this.customerType == 'existing' && this.order.contactId != null) || this.customerType == 'new'
    },
    isNewCustomer() {
      return this.customerType == 'new'
    },
    customerContactId() {
      if(this.customerType == 'existing')
        return this.order.contactId
      return null
    },
    customerTaxRateId() {
      if(this.customerData.defaultTaxRateId)
        return this.customerData.defaultTaxRateId

      // Company default if not set in customer
      return this.$store.state.account.salesTaxRate.id
    },
    contactAddressLookup() {
      if(this.customerType == 'existing' && this.order.contactId) {
        if(this.customerData.addresses) {
          var addressLookup = this.customerData.addresses.map(address => {
            return {
              name: address.formattedAddress,
              value: address.id,
              labelList: address.labelList,
              deliveryLabelList: address.deliveryLabelList
            }
          })
          addressLookup = _.concat(addressLookup, { name: 'Custom Address', value: null, labelList: [], deliveryLabelList: [] })
          return addressLookup
        }
      }
      return []
    },
    isAddressEditable() {
      if(this.customerType == 'existing' && this.order.contactId) {
        return this.order.contactAddressId == null
      }
      return true
    },
    addressDeliveryLabelList() {
      if(this.customerType == 'existing' && this.order.contactId) {
        if(this.order.contactAddressId == null) {
          return [{ name: 'Onsite', colour: this.$glass.orderOnsiteLabelColour }]
        }
        let addressData = this.selectedAddressData
        if(addressData && addressData.deliveryLabelList)
          return addressData.deliveryLabelList
      }
      return []
    },
    contactPersonLookup() {
      let lookupList = []

      if(this.customerType == 'existing' && this.order.contactId) { 
        if(this.customerData.persons) {
          lookupList = this.customerData.persons.map(person => {
            return {
              name: `${person.firstName} ${person.lastName}`,
              value: person.id,
              labelList: person.labelList
            }
          })
        }

        lookupList.push({
          value: null,
          name: 'Custom Contact Person'
        })
      }
      return lookupList
    },
    selectedAddressData() {
      if(this.customerType == 'existing' && this.order.contactId) {
        if(this.customerData.addresses) {
          if(this.customerData.addresses)
            return this.customerData.addresses.find(address => address.id == this.order.contactAddressId)
        }
      }
      return null
    },
    isCustomContactPerson() {
      if(this.customerType == 'existing' && this.order.contactId) {
        return this.order.contactPersonId == null || this.order.contactPersonId == ''
      }
      return false
    },
    selectedPersonData() {
      if(this.customerType == 'existing' && this.order.contactId) {
        if(this.customerData.persons) {
          if(this.customerData.persons)
            return this.customerData.persons.find(person => person.id == this.order.contactPersonId)
        }
      }
      return null
    },
    customerDefaultDespatch() {
      if(this.order.contactId == null)
        return ''
      return Utils.getLookupName(DespatchTypeLookup, this.customerData.defaultDespatchType)
    },
    customerDefaultLocation() {
      if(this.order.contactId == null)
        return ''
      return Utils.getLookupName(this.accountLocationLookup, this.customerData.accountLocationId)
    },
    isDespatchDelivery() {
      return this.order.despatchType == DespatchTypes.delivery
    },
    isDespatchPickup() {
      return this.order.despatchType == DespatchTypes.pickup
    },
    deliveryFeeNote() {
      return 'Delivery Fee of ' + this.$options.filters.formatCurrency(this.orderDeliveryFee) + ' added'
    },
    estimatedDespatchDate() {
      if(this.isDespatchDelivery)
        return this.$data.estimates.estimatedDeliveryDate

      return this.$data.estimates.estimatedPickupDate
    },
    requiredEarlierDateTime() {
      return moment(moment(this.order.requiredEarlierDate).local().format('YYYY-MM-DD') + ' ' + this.order.requiredEarlierTime)
    },
    orderDeliveryItem() {
      return this.order.orderItemList.find(item => item.isDeliveryItem)
    },
    isDeliveryItemPercentage() {
      let deliveryItem = this.orderDeliveryItem
      if(deliveryItem.uomType == UOMTypes.percentOfOrder || deliveryItem.uomType == UOMTypes.percentOfLineItem)
        return true
      return false
    },
    orderDeliveryFee() {
      let deliveryItem = this.orderDeliveryItem
      if(deliveryItem)
        return deliveryItem.linePrice
      return 0
    },
    accountCreditWarning() {
      if(this.isNewCustomer)
        return ''
      
      if(this.customerData.credit && this.customerData.credit.creditType == CreditTypes.account) {
        switch(this.customerData.credit.creditStatus) {
          case CreditStatusTypes.onhold:
            return `Customer's credit status is currently on hold`
          case CreditStatusTypes.stop:
            return `Customer's credit status is currently stopped`
          case CreditStatusTypes.active:
            if(this.summaryTotals.total > this.customerData.credit.availableCredit) {
              let overCredit = this.summaryTotals.total - this.customerData.credit.availableCredit
              return `Order is ${this.$options.filters.formatCurrency(overCredit)} over the customer's available account credit`
            }
            break
        }        
      }
      return ''
    },
    productionWarningCount() {
      return this.reviewOrderItemList.filter(item => {
        if(item.productionError && item.productionError != '')
          return true
        if(item.productionConfigError && item.productionConfigError != '')
          return true
        if(item.productionStockError && item.productionStockError != '')
          return true
        if(item.productionEditorError && item.productionEditorError != '')
          return true
        return false
      }).length
    },
    pricingWarningCount() {
      return this.reviewOrderItemList.filter(item => item.priceError != '' || item.priceLocalError != '').length
    },
    externalSetupCount() {
      return this.reviewOrderItemList.filter(item => item.isExternal).length
    },
    customerApprovalCount() {
      return this.reviewOrderItemList.filter(item => item.requiresCustomerApproval).length
    },
    orderHasNotices() {
      return this.productionWarningCount > 0 || 
        this.pricingWarningCount > 0 ||
        this.externalSetupCount > 0 ||
        this.customerApprovalCount > 0 || 
        this.accountCreditWarning != '' ||
        this.order.customerRequestedDate ||
        this.orderHasCustomerRefWarning
    },
    orderHasCustomerRefWarning() {
      return this.matchingCustomerRefList.length > 0 && !this.matchingCustomerRefAcceptWarning
    },
    isCustomerPricingSimple() {
      return this.accountPricingSystemType == PricingSystemTypes.simple
    },
    isOptionOrderEditorAvailable() {
      return this.userHasUpdatePermission(this.PermissionTypes.OrderEditorPermissionTypes.orderEditor)
    },
    editorDocumentFileList() {
      if(!this.order.uploadFileList)
        return []

      // Convert upload list to document list
      return this.order.uploadFileList.filter(upload => upload.uploaded).map(upload => {
        return {
          id: upload.key,
          name: upload.name,
          mimeType: upload.mimeType,
          url: upload.url
        }
      })
    },
    isCopiedFromOrder() {
      return !!this.$route.params.copyFromOrder && !!this.$route.params.sourceOrderId
    },
    isNCRFromOrder() {
      return !!this.$route.params.NCRFromOrder && !!this.$route.params.sourceOrderId
    }
  },
  methods: {
    handleSubmit(draft = false) {
      if(!this.$refs.documentUpload.validateUploadsComplete())
        return

      var orderData = _.cloneDeep(this.$data.order)
      orderData.isNewCustomer = this.customerType == 'new'
      orderData.submitDraft = draft

      // Set the date requested or given to the customer as the date
      orderData.dateEstimated = this.isDespatchDelivery ? this.estimates.estimatedDeliveryDate : this.estimates.estimatedPickupDate
      orderData.dateRequested = this.order.customerRequestedDate ? this.requiredEarlierDateTime : null

      orderData.deliveryTag = this.isDespatchDelivery ? this.addressDeliveryLabelList : []

      // Estimated date needs to be valid (might be still loading)
      if(!orderData.dateEstimated) {
        this.warningMessage('Estimate Date Not Set', 'Go to despatch tab to reload despatch estimates')
        return
      }

      // Need to accept the customer reference duplicate warning
      if(this.orderHasCustomerRefWarning) {
        this.warningMessage('Duplicate Customer Reference', 'Go to details tab and accept the duplicate customer reference warning to continue')
        return
      }

      // Tidy up data
      if(!this.order.customerRequestedDate)
        orderData.isUrgent = false

      // Calculate how long the order took to enter (for reporting / fee generation)
      // Rounded up to next minute
      var orderDuration = moment.duration(moment().diff(this.$data.order.orderEntryStartTime)).asSeconds()
      orderData.orderEntryDuration = Math.ceil(orderDuration / 60.0)

      // Build up the order item list for the server
      orderData.orderItemList = this.prepareOrderItemListForUpdate(this.reviewOrderItemList, { cleanupStructures: true })

      // If an NCR order & no non-delivery/non-min-invoice items have been copied over, stop the order creation
      if(this.isNCR) {
        var checkItems = orderData.orderItemList.filter(item => !item.isDeliveryItem && !item.isMinInvoiceItem)
        if(checkItems.length == 0) {
          this.warningMessage('No NCR items selected', 'No items have been selected for NCR. You must select the items for the NCR in the items step')
          return
        }
      }

      this.submitting = true
      orderData.newCustomerLabelList = orderData.newCustomerLabelList.map(label => label.id)

      this.$data.orderForm = this.clearFormResponse()

      this.generateOrderItemStandardEdgeworkDetails(this.reviewOrderItemList, this.edgeworkConfigList)
      this.generateOrderItemStandardDiagrams(this.reviewOrderItemList, this.orderStampConfig)

      // Copy over the list again now with diagrams
      orderData.orderItemList = this.prepareOrderItemListForUpdate(this.reviewOrderItemList, { cleanupStructures: true })

      this.$apiService.ordersService.createOrder(orderData, { useCompression: true })
      .then(response => {
        this.submitting = false

        // Clear editing flag to prevent route guard confirmation
        this.$data.isOrderEdited = false

        this.successToast('Order Created')
        this.$router.push(`/orders/${response.data.id}?created=true`)
      })
      .catch(err => {
        // Process errors
        this.$data.orderForm = this.processFormResponse(err)
        this.$refs.orderWizard.displayErrors()

        this.submitting = false
      })
    },
    requestData() {
      return new Promise((resolve, reject) => {

        return Promise.all([
          this.$apiService.contactService.getContactList({
            isActive: 1,
            isCustomer: 1
          }),
          this.$apiService.orderEditorService.getOrderEditorEdgeworkList({ include: 'orderEditorEdgeworkStock,orderEditorEdgeworkAllowance' }),
          this.$apiService.ordersService.getStampConfig(),
          this.$apiService.adminService.getAccountLocations(),
          this.$apiService.adminService.getTaxRateList()
        ])
        .then(results => {
          let contactList = results[0].data
          this.edgeworkConfigList = results[1].data
          this.orderStampConfig = results[2].data

          this.customerLookup = [{ value: null, name: 'None' }]
          this.customerLookup = _.concat(this.customerLookup, contactList.map(item => {
            return { value: item.id, name: item.name }
          }))

          this.accountLocationLookup = results[3].data.map(item => {
            return { value: item.id, name: item.name }
          })

          this.taxRateList = results[4].data

          return resolve()
        })
      })
    },
    requestCustomerDetails() {
      return new Promise((resolve, reject) => {
        if(this.order.contactId == null || this.order.contactId == '') {
          this.customerData = {}
          return resolve()
        }

        this.loadingCustomerData = true

        return this.$apiService.contactService.getContact(this.order.contactId)
        .then(result => {
          this.customerData = result.data
          this.order.contactName = this.customerData.name

          // Default delivery stock fee item
          this.deliveryStockId = this.customerData.defaultDeliveryStockId

          // Account location based on contact
          if(this.customerData.accountLocationId && !this.disableAccountLocationUpdate)
            this.order.accountLocationId = this.customerData.accountLocationId

          // Select the default address
          // Next tick to allow address list to be updated first
          this.$nextTick(() => {
            let defaultAddress = this.customerData.addresses.find(address => address.id == this.customerData.defaultAddressId)
            let defaultPerson = this.customerData.persons.find(person => person.id == this.customerData.defaultPersonId)
            this.order.contactAddressId = defaultAddress ? defaultAddress.id : null
            this.order.contactPersonId = defaultPerson ? defaultPerson.id : null

            // If an address has a 'delivery' label, select that instead
            let defaultDeliveryAddress = this.customerData.addresses.find(address => address.labelList && address.labelList.find(label => label.statusType == ContactLabelTypes.delivery) != null)
            if(defaultDeliveryAddress)
              this.order.contactAddressId = defaultDeliveryAddress.id
          })

          this.disableAccountLocationUpdate = false

          return resolve()
        })
        .catch(err => {
          this.loadingCustomerData = false
          // TODO: Handle error
          return resolve()
        })
        .finally(() => {
          this.loadingCustomerData = false
        })
      })
    },
    newContactPerson() {
      this.displayModal(ContactPersonDialog, { 
        contactId: this.order.contactId,
        isCreateMode: true
      })
      .then(contactPersonId => {
        if(contactPersonId) {
          this.successToast('Person Added')
          this.requestCustomerDetails()
          .then(() => {
            // Automatically select the newly created person
            this.order.contactPersonId = contactPersonId
          })
        }
      })
    },
    newContactAddress() {
      this.displayModal(ContactAddressDialog, { 
        contactId: this.order.contactId,
        isCreateMode: true
      })
      .then(contactAddressId => {
        if(contactAddressId) {
          this.successToast('Address Added')
          this.requestCustomerDetails()
          .then(() => {
            // Automatically select the newly created address
            this.order.contactAddressId = contactAddressId
          })
        }
      })
    },
    findItemRootInOrder(item) {
      var rootItemId = item.parentOrderItemId
      var nextParentId = rootItemId
      var foundParent = false
      while(!foundParent) {
        let parent = this.order.orderItemList.find(parentItem => parentItem.id == nextParentId)
        if(!parent)
          break
        rootItemId = parent.id
        nextParentId = parent.parentOrderItemId
      }
      return this.order.orderItemList.find(parentItem => parentItem.id == rootItemId)
    },
    updateDeliveryItemValue(value) {
      this.$refs.orderEntryList.updateDeliveryItemValue(value)
    },

    wizardStepChanged(tabIndex) {
      let largeOrderMinItemCount = 10

      // If despatch/review selected, refresh estimate dates from server
      // Uses processing dialog for larger orders
      if(tabIndex == 2 || tabIndex == 3) {

        if(this.order.orderItemList.length > largeOrderMinItemCount) {
          this.displayProcessingDialog({
            title: 'Updating',
            message: 'Updating and querying order',
            icon: 'fa-sync',
            onBeforeOpen: (instance) => {

              // Hack: Slight delay to allow the dialog to display before starting the copying process (which may take a while)
              setTimeout(() => {
                this.updateReviewItemList()
                this.debouncedOrderEstimateQuery()
                instance.hide()
              }, 250)
            }
          })
        } else {
          this.updateReviewItemList()
          this.debouncedOrderEstimateQuery()
        }
      }
    },

    displayDetailsTab() {
      this.$refs.orderWizard.selectTab(0)
    },
    displayItemsTab() {
      this.$refs.orderWizard.selectTab(1)
    },
    displayDespatchTab() {
      this.$refs.orderWizard.selectTab(2)
    },
    

    queryMatchingCustRef() {
      if(this.$data.order.customerRef == '' || this.customerContactId == null) {
        this.loadingMatchingCustomerRefList = false
        this.matchingCustomerRefList = []
        this.matchingCustomerRefTotalCount = 0
        this.matchingCustomerRefAcceptWarning = false
        return
      }
      let queryParams = {
        filter: {
          contactId: this.customerContactId,
          customerRef: this.$data.order.customerRef
        }
      }

      let maxOrderDisplayCount = 5

      this.loadingMatchingCustomerRefList = true
      this.$apiService.ordersService.getOrderFilteredList(queryParams)
      .then(response => {
        this.matchingCustomerRefTotalCount = response.data.length
        this.matchingCustomerRefList = response.data.slice(0, maxOrderDisplayCount).map(order => {
          return {
            id: order.id,
            type: order.type,
            name: order.sequenceNumber || 'Pending',
            status: order.status,
            customerRef: order.customerRef
          }
        })
      })
      .catch(err => {
        console.error(err)
      })
      .finally(() => {
        this.loadingMatchingCustomerRefList = false
        this.matchingCustomerRefAcceptWarning = false
      })
    },

    // Query order date estimates
    queryOrderEstimates() {
      this.$data.estimates.loadingEstimates = true
      this.$data.estimates.loadingError = ''
      this.$data.estimates.loadingErrorDetails = ''
      this.$data.estimates.afterDayCutoff = false
      this.$data.estimates.requiresProductionTransfers = false
      this.$data.estimates.requiresDespatchTransfer = false

      if(this.reviewOrderItemList.length == 0) {
        this.$data.estimates.loadingEstimates = false
        this.$data.estimates.loadingError = 'No order items set to calculate dates'
        return
      }

      var orderData = {
        type: this.order.type,
        accountLocationId: this.order.accountLocationId,
        orderItemList: this.prepareOrderItemListForUpdate(this.reviewOrderItemList, {
          cleanupStructures: true,
          cleanupEditor: true
        })
      }

      // Send all data (order info, order items) to server to calculate estimates
      this.$apiService.ordersService.reviewOrder(orderData, { useCompression: true })
      .then(response => {
        this.$data.estimates.estimatedPickupDate = response.data.estimatedPickupDate
        this.$data.estimates.estimatedDeliveryDate = response.data.estimatedDeliveryDate        
        this.$data.estimates.afterDayCutoff = response.data.afterDayCutoff
        this.$data.estimates.requiresProductionTransfers = response.data.requiresProductionTransfers
        this.$data.estimates.requiresDespatchTransfer = response.data.requiresDespatchTransfer

        // Apply any production warnings to order items
        if(_.isArray(response.data.orderItemWarnings)) {
          this.order.orderItemList.forEach((item, index) => {
            item.productionConfigError = ''

            let warning = response.data.orderItemWarnings.find(orderItemWarning => orderItemWarning.orderItemId == item.id)
            if(warning)
              item.productionConfigError = warning.productionConfigWarnings

            this.$set(this.order.orderItemList, index, item)
          })
        }
      })
      .catch(err => {
        this.$data.estimates.loadingError = 'Error querying estimates. Check item list'
        this.$data.estimates.loadingErrorDetails = this.processFormResponse(err).errorMessage || 'Unknown Error'
      })
      .finally(() => {
        this.$data.estimates.loadingEstimates = false
      })
    },

    onBeforeUnload: function(event) {
      // Ignore for local dev
      if(this.$glass.isLocalSite)
        return null
      if(!this.$data.isOrderEdited)
        return null

      var confirmationMessage = `You have unsaved changes with this new ${this.orderTypeName}. Are you sure you want to exit?`

      event.returnValue = confirmationMessage
      return confirmationMessage
    },

    updateReviewItemList() {
      if(this.isNCR) {
        // Only display unlocked NCR items
        this.reviewOrderItemList = _.cloneDeep(this.order.orderItemList.filter(item => {
          if(!item.isNCRLocked)
            return true
          // Check root if NCR unlocked
          if(item.parentOrderItemId) {
            let itemRoot = this.findItemRootInOrder(item)
            if(itemRoot && !itemRoot.isNCRLocked)
              return true
          }
          return false
        }))
      } else {
        this.reviewOrderItemList = _.cloneDeep(this.order.orderItemList)
      }

      // Update edgework info & standard diagrams on review order item list
      this.generateOrderItemStandardEdgeworkDetails(this.reviewOrderItemList, this.edgeworkConfigList)
      this.generateOrderItemStandardDiagrams(this.reviewOrderItemList, this.orderStampConfig)
    },

    copySourceOrder(sourceOrderId) {
      this.$nextTick(() => {
        this.displayProcessingDialog({
          title: 'Copying Order',
          message: 'Loading order',
          icon: 'fa-copy',
          onBeforeOpen: (instance) => {

            // Download an existing order & copy in the data
            this.$apiService.ordersService.getOrder(sourceOrderId)
            .then(async (response) => {
              // Copy over all applicable data
              this.order.type = response.data.type
              this.order.contactId = response.data.contactId

              // Query contact details to build associated data
              await this.requestCustomerDetails()

              this.order.contactPersonId = response.data.contactPersonId
              this.order.notes = response.data.notes
              this.order.customerRef = response.data.customerRef
              this.order.despatchType = response.data.despatchType
              
              // Force account location from the order source, not linked customer
              this.disableAccountLocationUpdate = true
              this.order.accountLocationId = response.data.accountLocationId

              this.order.customContactName = response.data.customContactName || ''
              this.order.customContactEmail = response.data.customContactEmail || ''
              this.order.customContactMobile = response.data.customContactMobile || ''
              this.order.customContactDetails = response.data.customContactDetails || ''

              if(this.order.despatchType == DespatchTypes.delivery) {
                this.order.contactAddressId = response.data.contactAddressId

                this.order.deliveryAddress1 = response.data.deliveryAddress1
                this.order.deliveryAddress2 = response.data.deliveryAddress2
                this.order.deliverySuburb = response.data.deliverySuburb
                this.order.deliveryState = response.data.deliveryState
                this.order.deliveryPostcode = response.data.deliveryPostcode
                this.order.deliveryCountry = response.data.deliveryCountry
              }

              // Copy order items
              this.$refs.orderEntryList.copyOrderItemList(_.cloneDeep(response.data.orderItems))
            })
            .catch(err => {
              this.displayNetworkError(err, 'Error Copying Order', 'Could not copy the order')
            })
            .finally(() => {
              instance.hide()
            })
          }
        })
      })
    },

    ncrFromSourceOrder(sourceOrderId) {
      this.$nextTick(() => {
        this.displayProcessingDialog({
          title: 'Creating NCR',
          message: 'Loading order',
          icon: 'fa-repeat',
          onBeforeOpen: (instance) => {

            // Download an existing order & copy in the data
            this.$apiService.ordersService.getOrder(sourceOrderId)
            .then(async (response) => {
              // Setup NCR mode
              this.customerType = 'existing'
              this.order.type = OrderTypes.nonChargeableRework
              this.order.originalOrderId = response.data.id

              // Copy over all applicable data
              this.order.contactId = response.data.contactId

              // Query contact details to build associated data
              await this.requestCustomerDetails()

              this.order.contactPersonId = response.data.contactPersonId
              this.order.notes = response.data.notes || ''
              this.order.customerRef = response.data.customerRef
              this.order.despatchType = response.data.despatchType
              
              // Force account location from the order source, not linked customer
              this.disableAccountLocationUpdate = true
              this.order.accountLocationId = response.data.accountLocationId

              if(this.order.despatchType == DespatchTypes.delivery) {
                this.order.contactAddressId = response.data.contactAddressId

                this.order.deliveryAddress1 = response.data.deliveryAddress1
                this.order.deliveryAddress2 = response.data.deliveryAddress2
                this.order.deliverySuburb = response.data.deliverySuburb
                this.order.deliveryState = response.data.deliveryState
                this.order.deliveryPostcode = response.data.deliveryPostcode
                this.order.deliveryCountry = response.data.deliveryCountry
              }

              if(response.data.sequenceNumber) {
                if(this.order.notes)
                  this.order.notes += '\n'
                this.order.notes += `NCR based on order #${response.data.sequenceNumber}`
              }

              // Copy order items
              this.$refs.orderEntryList.copyOrderItemList(_.cloneDeep(response.data.orderItems), null, -1, { isNCR: true })
            })
            .catch(err => {
              this.displayNetworkError(err, 'Error loading Order', 'Could not load the order')
            })
            .finally(() => {
              instance.hide()
            })
          }
        })
      })
    },
    copyRecoveryOrder(orderRecoveryId) {
      this.$nextTick(() => {
        this.displayProcessingDialog({
          title: 'Recovering Order',
          message: 'Loading order data',
          icon: 'fa-database',
          onBeforeOpen: (instance) => {

            this.$apiService.ordersService.getOrderRecovery(orderRecoveryId)
            .then(async (response) => {

              var recoveryInfo = response.data
              var orderData = JSON.parse(response.data.orderData)

              this.customerType = 'existing'
              this.order.type = orderData.type

              // Copy over all applicable data
              this.order.contactId = recoveryInfo.contactId

              // Query contact details to build associated data
              await this.requestCustomerDetails()

              this.order.contactPersonId = orderData.contactPersonId || 0
              this.order.notes = orderData.notes || ''
              this.order.customerRef = orderData.customerRef || ''

              // Copy order items
              this.$refs.orderEntryList.copyOrderItemList(orderData.orderItems, null, -1)

              // Set recovery UUID from existing to reuse it / remove it when creating order
              this.order.recoveryUUID = recoveryInfo.recoveryUUID

            })
            .catch(err => {
              this.displayNetworkError(err, 'Error Restoring Order', 'Could not restore the order')
            })
            .finally(() => {
              instance.hide()
            })
          }
        })
      })
    },

    startOrderEditor() {
      this.showOrderEditor = true
    },
    finishOrderEditor() {
      this.showOrderEditor = false

      this.$refs.orderEntryList.copyOrderItemList(_.cloneDeep(this.order.orderItemList), null, -1, { isEditorItemCopy: true })
    },
    importSourceOrderDocuments() {
      this.displayProcessingDialog({
        title: 'Importing',
        message: 'Downloading documents from source order',
        icon: 'fa-sync',
        onBeforeOpen: (instance) => {

          // Get files
          this.$apiService.coreService.getUserFiles(LinkTypes.order, this.$route.params.sourceOrderId).then(response => {
            // Add as remote files to FormUpload
            response.data.forEach(file => {
              this.$refs.documentUpload.addRemoteFile({
                name: file.name,
                type: file.mimeType,
                url: file.url
              })
            })

            instance.hide()
          })
          .catch(err => {
            console.error(err)
            instance.hide()
          })
          
        }
      })

    }
  }
};
</script>
